import { useMemo } from "react";
import { ColDef } from "ag-grid-community";
import {
  DataEntitiesReportsRedeemedValidationReport,
  ReportsApi,
  ServerTransferSavedReportResponse,
} from "../shared/api/axios-client";
import { BaseReport } from "./BaseReport";
import { showMoney, cents } from "../Data/Money";
import { ReportConfig } from "./types";
import { CheckCircle } from "react-bootstrap-icons";
import { createApiService } from "../shared/apiConfig";
import { toLocalDateTime } from "../Data/Date";

const reportApi = createApiService(ReportsApi);

interface RedeemedValidationFilter {
  page: number;
  pageSize: number;
  facilityId?: number;
  from?: string;
  to?: string;
}

const getColumns =
  (): ColDef<DataEntitiesReportsRedeemedValidationReport>[] => [
    {
      field: "name",
      headerName: "Validation Name",
      filter: false,
    },
    {
      field: "endedWhen",
      headerName: "Date",
      filter: false,
      valueFormatter: (p) => toLocalDateTime(p.value),
    },
    {
      field: "originalPrice",
      headerName: "Original Price",
      filter: false,
      valueFormatter: (p) => showMoney(p.value),
    },
    {
      field: "validatedPrice",
      headerName: "Validated Price",
      filter: false,
      valueFormatter: (p) => showMoney(p.value),
    },
  ];

export function RedeemedValidationReport({
  facilityId,
  savedReports,
  setSavedReports,
  heightOffset = 50,
}: {
  facilityId?: number;
  savedReports: ServerTransferSavedReportResponse[];
  setSavedReports: React.Dispatch<
    React.SetStateAction<ServerTransferSavedReportResponse[]>
  >;
  heightOffset?: number;
}) {
  const config = useMemo<
    ReportConfig<
      DataEntitiesReportsRedeemedValidationReport,
      RedeemedValidationFilter
    >
  >(
    () => ({
      id: "redeemedvalidation",
      title: "Redeemed Validations",
      icon: <CheckCircle style={{ fontSize: "1.5rem", marginTop: "-10px" }} />,
      description: `
        <p>This report shows every redeemed validation and how it affected the final price.</p>
        <ul>
          <li>Discount-based validations only</li>
          <li>Uses reservation end time</li>
          <li>Shows price before and after validation if validation was applied after purchase</li>
        </ul>
      `,
      defaultColumns: getColumns(),
      filterFields: [],
      defaultFilterForm: {
        from: "",
        to: "",
        quickDateMode: "All",
      },
      getData: async (filter) => {
        const response = await reportApi.monitorReportsRedeemedValidationGet({
          ...filter,
        });

        return {
          items: response.data?.items ?? [],
          total: response.data?.items?.length ?? 0,
        };
      },
      transformData: (rows) =>
        rows.map((r) => ({
          ...r,
          originalPrice: cents(r.originalPrice),
          validatedPrice: cents(r.validatedPrice),
        })),
    }),
    [],
  );

  return (
    <BaseReport
      facilityId={facilityId}
      config={config}
      savedReports={savedReports}
      setSavedReports={setSavedReports}
      heightOffset={heightOffset}
      storageKey="report_columns_redeemed_validations"
    />
  );
}
